import { GUI } from '../../../node_modules/dat.gui/build/dat.gui.module.js';
import { eventBus } from '../utils/EventEmitter.js';
import { fadeIn, fadeOut } from '../utils/dom.js';

/**
 * Manages UI controls and interactions
 */
export class UIManager {
    constructor(effectsManager, canvasManager) {
        this.effectsManager = effectsManager;
        this.canvasManager = canvasManager;
        this.gui = null;
        this.lastValues = new Map();
        this.isUpdatingControls = false;  // Add flag to prevent circular updates
        this.setupShaderSelector();
        this.setupGUI();
        this.setupEventListeners();
        this.setupUrlModal();
        this.setupImageUpload();
        this.setupCameraButton();
        this.setupAudioButton();
        this.setupFullscreenButton();
        this.setupEffectsUI();
        this.setupExportButtons();
        this.setupProgressBar();
    }

    /**
     * Set up shader selector
     */
    setupShaderSelector() {
        // Get effect picker button
        const effectPickerButton = document.getElementById('effectPickerButton');
        const currentEffectText = effectPickerButton.querySelector('.current-effect');

        // Create effects modal
        const modalOverlay = document.createElement('div');
        modalOverlay.className = 'effects-modal-overlay';
        
        // Define effect categories with descriptions
        const categories = {
            'Display Effects': {
                'crt': 'Classic CRT monitor effect with screen curvature and scan lines',
                'glitch': 'Digital glitch effect with RGB shifting and noise',
                'scanlines': 'Retro screen scan lines with adjustable intensity',
                'phosphor': 'CRT phosphor glow and persistence effect',
                'sync-failure': 'Vertical sync failure simulation'
            },
            'Visual Effects': {
                'canyon': 'Dynamic canyon landscape with atmospheric lighting',
                'film-grain': 'Classic film grain and noise effect',
                'chromatic-aberration': 'Color channel separation effect',
                'protean-clouds': 'Mesmerizing volumetric cloud formations with dynamic movement',
                'spiral': 'Hypnotic spiral fractal with dynamic particles and distortions'
            },
            'Environmental': {
                'fog': 'Atmospheric fog with depth',
                'heat-distortion': 'Heat wave distortion effect',
                'universe': 'Mesmerizing network of pulsing nodes and connections in a cosmic space',
                'god-rays': 'Volumetric light rays effect',
                'neon': 'Neon glow and bloom effect'
            },
            'Special Effects': {
                'pixel-dissolve': 'Pixel-by-pixel dissolve transition',
                'wave-distortion': 'Wave-like image distortion',
                'matrix': 'The Matrix digital rain effect',
                'water-ripples': 'Water ripple distortion effect',
                'outrun': 'Retro synthwave landscape with dynamic sun and grid',
                'drive-home': 'Atmospheric rainy night drive with bokeh lights'
            }
        };

        // Create modal content
        const modalContent = document.createElement('div');
        modalContent.className = 'effects-modal';
        modalContent.innerHTML = '<h2>Select Effect</h2>';

        // Add close button
        const closeButton = document.createElement('button');
        closeButton.className = 'effects-modal-close';
        closeButton.innerHTML = '×';
        modalContent.appendChild(closeButton);

        // Create categories
        Object.entries(categories).forEach(([categoryName, effects]) => {
            const categoryDiv = document.createElement('div');
            categoryDiv.className = 'effects-category';
            categoryDiv.innerHTML = `<h3>${categoryName}</h3>`;

            const effectsGrid = document.createElement('div');
            effectsGrid.className = 'effects-grid';

            Object.entries(effects).forEach(([effectId, description]) => {
                const isImplemented = this.effectsManager.hasEffect(effectId);
                const card = document.createElement('div');
                card.className = `effect-card${isImplemented ? '' : ' disabled'}${effectId === 'glitch' ? ' active' : ''}`;
                card.dataset.effect = effectId;

                card.innerHTML = `
                    <div class="effect-info">
                        <div class="effect-name">${this.formatEffectName(effectId)}</div>
                        <div class="effect-description">${description}</div>
                    </div>
                `;

                if (isImplemented) {
                    // Handle hover preview
                    let originalEffect = null;
                    card.addEventListener('mouseenter', () => {
                        originalEffect = this.effectsManager.getActiveEffect();
                        this.effectsManager.setActiveEffect(effectId);
                    });

                    card.addEventListener('mouseleave', () => {
                        if (originalEffect) {
                            this.effectsManager.setActiveEffect(originalEffect.getName());
                            originalEffect = null;
                        }
                    });

                    // Handle click
                    card.addEventListener('click', () => {
                        // Update active state
                        effectsGrid.querySelectorAll('.effect-card').forEach(c => c.classList.remove('active'));
                        card.classList.add('active');

                        // Update current effect display
                        currentEffectText.textContent = this.formatEffectName(effectId);

                        // Switch shader and keep it as active
                        this.effectsManager.setActiveEffect(effectId);
                        originalEffect = null; // Clear the original effect so mouseleave won't revert it

                        // Close modal
                        modalOverlay.classList.remove('show');
                        document.body.style.overflow = '';  // Restore body scrolling

                        // Track effect change
                        if (typeof gtag !== 'undefined') {
                            gtag('event', 'change_effect', {
                                'event_category': 'Effect',
                                'event_label': effectId
                            });
                        }
                    });
                }

                effectsGrid.appendChild(card);
            });

            categoryDiv.appendChild(effectsGrid);
            modalContent.appendChild(categoryDiv);
        });

        modalOverlay.appendChild(modalContent);
        document.body.appendChild(modalOverlay);

        // Show modal when clicking effect picker button
        effectPickerButton.addEventListener('click', () => {
            modalOverlay.classList.add('show');
            document.body.style.overflow = 'hidden';  // Prevent body scrolling
        });

        // Hide modal when clicking close button or outside
        closeButton.addEventListener('click', () => {
            const activeEffect = this.effectsManager.getActiveEffect();
            if (activeEffect) {
                this.effectsManager.setActiveEffect(activeEffect.getName());
            }
            modalOverlay.classList.remove('show');
            document.body.style.overflow = '';  // Restore body scrolling
        });

        modalOverlay.addEventListener('click', (e) => {
            if (e.target === modalOverlay) {
                const activeEffect = this.effectsManager.getActiveEffect();
                if (activeEffect) {
                    this.effectsManager.setActiveEffect(activeEffect.getName());
                }
                modalOverlay.classList.remove('show');
                document.body.style.overflow = '';  // Restore body scrolling
            }
        });

        // Close modal on escape key
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && modalOverlay.classList.contains('show')) {
                const activeEffect = this.effectsManager.getActiveEffect();
                if (activeEffect) {
                    this.effectsManager.setActiveEffect(activeEffect.getName());
                }
                modalOverlay.classList.remove('show');
                document.body.style.overflow = '';  // Restore body scrolling
            }
        });

        // Listen for effect changes
        eventBus.on('effect:activated', ({ name }) => {
            currentEffectText.textContent = this.formatEffectName(name);
            modalOverlay.querySelectorAll('.effect-card').forEach(card => {
                card.classList.toggle('active', card.dataset.effect === name);
            });
        });
    }

    formatEffectName(effect) {
        return effect
            .split('-')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    }

    /**
     * Set up dat.gui interface
     */
    setupGUI() {
        this.gui = new GUI({
            autoPlace: false,
            width: '100%'
        });

        // Create GUI container
        const guiContainer = document.createElement('div');
        guiContainer.className = 'gui-container';
        guiContainer.appendChild(this.gui.domElement);

        // Add to preview section
        const previewSection = document.querySelector('.preview-section');
        previewSection.insertBefore(guiContainer, document.querySelector('.export-controls'));

        // Add effect controls
        this.updateEffectControls();
    }

    /**
     * Set up event listeners
     */
    setupEventListeners() {
        eventBus.on('effect:activated', ({ name }) => {
            this.updateEffectControls();
        });

        eventBus.on('effect:updated', ({ name, params }) => {
            this.updateControlValues(name, params);
        });

        // Handle sidebar toggle
        const sidebarToggle = document.getElementById('sidebarToggle');
        const controlsPanel = document.getElementById('controlsPanel');

        sidebarToggle.addEventListener('click', () => {
            controlsPanel.classList.toggle('show');
        });

        // Close sidebar when clicking outside on mobile
        document.addEventListener('click', (e) => {
            if (window.innerWidth <= 768 && 
                !controlsPanel.contains(e.target) && 
                !sidebarToggle.contains(e.target) &&
                controlsPanel.classList.contains('show')) {
                controlsPanel.classList.remove('show');
            }
        });

        // Close sidebar when resizing above mobile breakpoint
        window.addEventListener('resize', () => {
            if (window.innerWidth > 768) {
                controlsPanel.classList.remove('show');
            }
        });

        // Set up download button and modal
        this.setupDownloadButton();
    }

    /**
     * Set up download button and modal
     */
    setupDownloadButton() {
        const downloadButton = document.getElementById('downloadButton');
        const modalOverlay = document.querySelector('.download-modal-overlay');
        const downloadOptions = modalOverlay.querySelectorAll('.download-option');

        // Update video format text based on SharedArrayBuffer support
        const videoOption = modalOverlay.querySelector('.download-option[data-format="video"]');
        if (videoOption) {
            const formatText = videoOption.querySelector('.download-option-format');
            if (formatText) {
                // Check if SharedArrayBuffer is supported
                const hasSharedArrayBufferSupport = typeof SharedArrayBuffer !== 'undefined';
                formatText.textContent = hasSharedArrayBufferSupport ? 'High quality MP4' : 'WebM video';
            }
        }

        // Show modal when clicking download button
        downloadButton.addEventListener('click', async () => {
            // Exit fullscreen if active
            if (document.fullscreenElement) {
                await document.exitFullscreen();
            }
            modalOverlay.classList.add('show');
        });

        // Hide modal when clicking outside
        modalOverlay.addEventListener('click', (e) => {
            if (e.target === modalOverlay) {
                modalOverlay.classList.remove('show');
            }
        });

        // Handle download option clicks
        downloadOptions.forEach(option => {
            option.addEventListener('click', () => {
                const format = option.dataset.format;
                if (format === 'gif') {
                    eventBus.emit('export:gif');
                } else if (format === 'video') {
                    eventBus.emit('export:video');
                }
                modalOverlay.classList.remove('show');
            });
        });

        // Close modal on escape key
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && modalOverlay.classList.contains('show')) {
                modalOverlay.classList.remove('show');
            }
        });

        // Show/hide download button with fullscreen button
        const canvasContainer = document.querySelector('.canvas-container');
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
                    const isFullscreen = document.fullscreenElement === canvasContainer;
                    downloadButton.style.opacity = isFullscreen ? '0' : '1';
                }
            });
        });

        observer.observe(canvasContainer, {
            attributes: true
        });

        // Show download button on hover in fullscreen
        canvasContainer.addEventListener('mousemove', () => {
            if (document.fullscreenElement === canvasContainer) {
                downloadButton.style.opacity = '1';
                setTimeout(() => {
                    if (document.fullscreenElement === canvasContainer) {
                        downloadButton.style.opacity = '0';
                    }
                }, 2000);
            }
        });
    }

    /**
     * Update effect controls in GUI
     */
    updateEffectControls() {
        // Clear existing controls
        this.gui.destroy();
        this.gui = new GUI({
            autoPlace: false,
            width: '100%'
        });

        const activeEffect = this.effectsManager.getActiveEffect();
        if (!activeEffect) return;

        const config = activeEffect.getUIConfig();
        const params = activeEffect.getParams();

        // Create controls for each parameter
        Object.entries(config).forEach(([param, settings]) => {
            const controller = settings.min !== undefined && settings.max !== undefined
                ? this.gui.add(params, param, settings.min, settings.max, settings.step)
                : this.gui.add(params, param);

            controller.name(settings.label || param);
            
            // Store initial value
            this.lastValues.set(param, params[param]);

            controller.onChange((value) => {
                // Only track significant changes
                const lastValue = this.lastValues.get(param);
                const threshold = this.getChangeThreshold(param);
                
                if (Math.abs(value - lastValue) > threshold) {
                    this.trackParameterChange(param, value);
                    this.lastValues.set(param, value);
                }

                // Update effect parameters and save configuration
                this.effectsManager.updateEffectParams(activeEffect.getName(), { [param]: value });
            });
        });

        // Update GUI container
        const guiContainer = document.querySelector('.gui-container');
        guiContainer.innerHTML = '';
        guiContainer.appendChild(this.gui.domElement);
    }

    /**
     * Get threshold for tracking parameter changes
     * @param {string} param - Parameter name
     * @returns {number} Change threshold
     */
    getChangeThreshold(param) {
        const thresholds = {
            rgbShift: 0.002,
            rgbAngle: 30,
            glitchIntensity: 0.1,
            scanlineIntensity: 0.1,
            curvature: 0.1,
            cornerDarkness: 0.1,
            scanlineDensity: 10,
            phosphorGlow: 0.1,
            colorBleeding: 0.1,
            flickerIntensity: 0.01
        };
        return thresholds[param] || 0.1;
    }

    /**
     * Track parameter changes for analytics
     * @param {string} param - Parameter name
     * @param {any} value - New value
     */
    trackParameterChange(param, value) {
        if (typeof gtag !== 'undefined') {
            gtag('event', 'adjust_effect', {
                'event_category': 'Effect',
                'event_label': param,
                'value': typeof value === 'boolean' ? (value ? 1 : 0) : Math.round(value * 100)
            });
        }
    }

    /**
     * Update control values without triggering events
     * @param {string} effectName - Effect name
     * @param {Object} params - New parameters
     */
    updateControlValues(effectName, params) {
        if (this.isUpdatingControls) return;  // Prevent circular updates
        
        const activeEffect = this.effectsManager.getActiveEffect();
        if (activeEffect && activeEffect.getName() === effectName) {
            this.isUpdatingControls = true;
            Object.entries(params).forEach(([param, value]) => {
                const controller = this.gui.__controllers.find(c => c.property === param);
                if (controller) {
                    controller.setValue(value);
                }
            });
            this.isUpdatingControls = false;
        }
    }

    /**
     * Show progress bar
     * @param {string} text - Initial progress text
     */
    showProgress(text) {
        const progressBar = document.getElementById('exportProgress');
        progressBar.style.display = 'block';
        this.updateProgress(0, text);
    }

    /**
     * Hide progress bar
     */
    hideProgress() {
        const progressBar = document.getElementById('exportProgress');
        progressBar.style.display = 'none';
    }

    /**
     * Update progress bar
     * @param {number} progress - Progress percentage (0-100)
     * @param {string} text - Progress text
     */
    updateProgress(progress, text) {
        const progressBar = document.getElementById('exportProgress');
        const progressFill = progressBar.querySelector('.progress-fill');
        const progressText = progressBar.querySelector('.progress-text');

        progressFill.style.width = `${progress}%`;
        progressText.textContent = text || `${Math.round(progress)}%`;
    }

    /**
     * Get recent URLs from localStorage
     * @returns {Array} Array of recent URLs
     */
    getRecentUrls() {
        try {
            const urls = JSON.parse(localStorage.getItem('recentUrls')) || [];
            return Array.isArray(urls) ? urls : [];
        } catch {
            return [];
        }
    }

    /**
     * Add URL to recent URLs list
     * @param {string} url - URL to add
     */
    addRecentUrl(url) {
        const urls = this.getRecentUrls();
        // Remove if already exists
        const index = urls.indexOf(url);
        if (index > -1) {
            urls.splice(index, 1);
        }
        // Add to front of array
        urls.unshift(url);
        // Keep only last 10 URLs
        urls.splice(10);
        localStorage.setItem('recentUrls', JSON.stringify(urls));
        localStorage.setItem('lastUrl', url);
    }

    /**
     * Create recent URLs list element
     * @returns {HTMLElement} Recent URLs list element
     */
    createRecentUrlsList() {
        const urls = this.getRecentUrls();
        if (urls.length === 0) return null;

        const container = document.createElement('div');
        container.className = 'recent-urls';
        container.style.cssText = 'margin-top: 10px; border-top: 1px solid var(--border-color); padding-top: 10px;';

        const title = document.createElement('div');
        title.textContent = 'Recent URLs';
        title.style.cssText = 'font-size: 0.9rem; opacity: 0.7; margin-bottom: 8px;';
        container.appendChild(title);

        const list = document.createElement('div');
        list.style.cssText = 'display: flex; flex-direction: column; gap: 4px;';

        urls.forEach(url => {
            const urlItem = document.createElement('a');
            urlItem.href = '#';
            urlItem.textContent = this.truncateUrl(url);
            urlItem.title = url;
            urlItem.style.cssText = 'color: var(--accent-color); font-size: 0.9rem; text-decoration: none; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;';
            urlItem.addEventListener('click', (e) => {
                e.preventDefault();
                eventBus.emit('image:loadUrl', { url });
                this.hideUrlModal();
            });
            list.appendChild(urlItem);
        });

        container.appendChild(list);
        return container;
    }

    /**
     * Truncate URL for display
     * @param {string} url - URL to truncate
     * @returns {string} Truncated URL
     */
    truncateUrl(url) {
        try {
            const urlObj = new URL(url);
            const path = urlObj.pathname;
            const filename = path.split('/').pop();
            return filename || urlObj.hostname;
        } catch {
            return url.length > 30 ? url.substring(0, 27) + '...' : url;
        }
    }

    /**
     * Set up URL input modal
     */
    setupUrlModal() {
        // Create URL link
        const uploadLabel = document.querySelector('.upload-label');
        const urlLink = document.createElement('a');
        urlLink.href = '#';
        urlLink.className = 'url-link';
        urlLink.textContent = 'or link to an image on the web';
        uploadLabel.parentElement.insertBefore(urlLink, uploadLabel.nextSibling);

        // Create modal
        const modalOverlay = document.createElement('div');
        modalOverlay.className = 'modal-overlay';
        modalOverlay.innerHTML = `
            <div class="modal">
                <h3>Enter Image URL</h3>
                <input type="text" id="imageUrl" placeholder="https://example.com/image.jpg" />
                <div class="modal-buttons">
                    <button class="cancel">Cancel</button>
                    <button class="load">Load Image</button>
                </div>
            </div>
        `;
        document.body.appendChild(modalOverlay);

        // Get modal elements
        const modal = modalOverlay.querySelector('.modal');
        const imageUrlInput = modalOverlay.querySelector('#imageUrl');
        const loadButton = modalOverlay.querySelector('.load');
        const cancelButton = modalOverlay.querySelector('.cancel');

        // Show modal
        urlLink.addEventListener('click', (e) => {
            e.preventDefault();
            modalOverlay.classList.add('show');
            imageUrlInput.value = ''; // Clear the input field
            
            // Focus input after a small delay to ensure modal is rendered
            setTimeout(() => {
                imageUrlInput.focus();
            }, 50);

            // Add recent URLs list
            const recentUrlsList = this.createRecentUrlsList();
            if (recentUrlsList) {
                const existingList = modal.querySelector('.recent-urls');
                if (existingList) {
                    existingList.remove();
                }
                modal.appendChild(recentUrlsList);
            }
        });

        // Hide modal
        this.hideUrlModal = () => {
            modalOverlay.classList.remove('show');
            imageUrlInput.value = '';
            const recentUrlsList = modal.querySelector('.recent-urls');
            if (recentUrlsList) {
                recentUrlsList.remove();
            }
        };

        // Handle modal interactions
        cancelButton.addEventListener('click', this.hideUrlModal);
        modalOverlay.addEventListener('click', (e) => {
            if (e.target === modalOverlay) this.hideUrlModal();
        });

        // Handle URL submission
        const handleUrlSubmit = () => {
            const url = imageUrlInput.value.trim();
            if (url) {
                this.addRecentUrl(url);
                eventBus.emit('image:loadUrl', { url });
                this.hideUrlModal();
            }
        };

        loadButton.addEventListener('click', handleUrlSubmit);
        imageUrlInput.addEventListener('keypress', (e) => {
            if (e.key === 'Enter') handleUrlSubmit();
        });

        // Close modal on escape key
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && modalOverlay.classList.contains('show')) {
                this.hideUrlModal();
            }
        });
    }

    /**
     * Set up image upload functionality
     */
    setupImageUpload() {
        const imageUpload = document.getElementById('imageUpload');
        const dropZone = document.querySelector('.upload-label');

        // Handle file input change
        imageUpload.addEventListener('change', (e) => {
            const file = e.target.files[0];
            if (!file) return;

            // Track image upload
            if (typeof gtag !== 'undefined') {
                gtag('event', 'upload_image', {
                    'event_category': 'Interaction',
                    'event_label': file.type,
                    'value': Math.round(file.size / 1024) // size in KB
                });
            }

            // Handle GIF or static image
            if (file.type === 'image/gif') {
                const gifUrl = URL.createObjectURL(file);
                eventBus.emit('image:loadGif', { url: gifUrl, shouldRevokeUrl: true });
            } else {
                const img = new Image();
                img.src = URL.createObjectURL(file);
                img.onload = () => {
                    eventBus.emit('image:loaded', img);
                    URL.revokeObjectURL(img.src);
                };
            }

            // Close sidebar on mobile
            if (window.innerWidth <= 768) {
                const controlsPanel = document.getElementById('controlsPanel');
                controlsPanel.classList.remove('show');
            }
        });

        // Handle drag and drop
        const preventDefaults = (e) => {
            e.preventDefault();
            e.stopPropagation();
        };

        const highlight = () => dropZone.classList.add('drag-over');
        const unhighlight = () => dropZone.classList.remove('drag-over');

        ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
            dropZone.addEventListener(eventName, preventDefaults, false);
            document.body.addEventListener(eventName, preventDefaults, false);
        });

        ['dragenter', 'dragover'].forEach(eventName => {
            dropZone.addEventListener(eventName, highlight, false);
        });

        ['dragleave', 'drop'].forEach(eventName => {
            dropZone.addEventListener(eventName, unhighlight, false);
        });

        dropZone.addEventListener('drop', (e) => {
            const dt = e.dataTransfer;
            const files = dt.files;

            if (files.length > 0 && files[0].type.startsWith('image/')) {
                imageUpload.files = files;
                imageUpload.dispatchEvent(new Event('change'));
            }
        });
    }

    /**
     * Set up camera button functionality
     */
    setupCameraButton() {
        const cameraButton = document.getElementById('cameraButton');
        const cameraSwitchButton = document.getElementById('cameraSwitchButton');
        
        if (!cameraButton) return;
        
        cameraButton.addEventListener('click', async () => {
            try {
                if (!this.isCameraActive) {
                    await this.canvasManager.startCamera();
                    this.isCameraActive = true;
                    cameraButton.classList.add('active');
                    cameraButton.querySelector('.camera-text').textContent = 'Stop Camera';
                } else {
                    this.canvasManager.stopCamera();
                    this.isCameraActive = false;
                    cameraButton.classList.remove('active');
                    cameraButton.querySelector('.camera-text').textContent = 'Use Camera';
                }
            } catch (error) {
                console.error('Camera error:', error);
                // Handle error (show message to user, etc.)
            }
        });

        // Set up camera switch button if available
        if (cameraSwitchButton) {
            cameraSwitchButton.addEventListener('click', async () => {
                try {
                    if (this.isCameraActive) {
                        await this.canvasManager.switchCamera();
                    }
                } catch (error) {
                    console.error('Camera switch error:', error);
                    // Handle error (show message to user, etc.)
                }
            });
        }

        // Listen for camera events
        eventBus.on('camera:started', () => {
            this.isCameraActive = true;
            cameraButton.classList.add('active');
            cameraButton.querySelector('.camera-text').textContent = 'Stop Camera';
        });

        eventBus.on('camera:stopped', () => {
            this.isCameraActive = false;
            cameraButton.classList.remove('active');
            cameraButton.querySelector('.camera-text').textContent = 'Use Camera';
        });

        eventBus.on('camera:error', (error) => {
            this.isCameraActive = false;
            cameraButton.classList.remove('active');
            cameraButton.querySelector('.camera-text').textContent = 'Use Camera';
            console.error('Camera error:', error);
            // Handle error (show message to user, etc.)
        });
    }

    /**
     * Set up audio button functionality
     */
    setupAudioButton() {
        const audioButton = document.getElementById('audioButton');
        
        audioButton.addEventListener('click', async () => {
            try {
                if (!audioButton.classList.contains('active')) {
                    // Start audio
                    await eventBus.emit('audio:start');
                    audioButton.classList.add('active');
                    audioButton.querySelector('.audio-text').textContent = 'Stop Audio';
                } else {
                    // Stop audio
                    eventBus.emit('audio:stop');
                    audioButton.classList.remove('active');
                    audioButton.querySelector('.audio-text').textContent = 'Use Audio';
                }
                
                // Close sidebar on mobile
                if (window.innerWidth <= 768) {
                    const controlsPanel = document.getElementById('controlsPanel');
                    controlsPanel.classList.remove('show');
                }
            } catch (error) {
                console.error('Audio error:', error);
                alert('Failed to access microphone. Please make sure you have granted microphone permissions.');
            }
        });
        
        // Handle audio events
        eventBus.on('audio:started', () => {
            audioButton.classList.add('active');
            audioButton.querySelector('.audio-text').textContent = 'Stop Audio';
        });
        
        eventBus.on('audio:stopped', () => {
            audioButton.classList.remove('active');
            audioButton.querySelector('.audio-text').textContent = 'Use Audio';
        });
        
        eventBus.on('audio:error', (error) => {
            audioButton.classList.remove('active');
            audioButton.querySelector('.audio-text').textContent = 'Use Audio';
            console.error('Audio error:', error);
            alert('Audio error: ' + error.message);
        });
    }

    /**
     * Set up fullscreen button functionality
     */
    setupFullscreenButton() {
        const fullscreenButton = document.getElementById('fullscreenButton');
        const canvasContainer = document.querySelector('.canvas-container');
        
        // Update button icon based on fullscreen state
        const updateFullscreenIcon = () => {
            const isFullscreen = document.fullscreenElement === canvasContainer;
            fullscreenButton.innerHTML = isFullscreen 
                ? '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M20 14h2v6h-6v-2h4v-4zM2 14h2v4h4v2H2v-6zM20 10h2V4h-6v2h4v4zM6 6V4H2v6h2V6h2z" fill="currentColor"/></svg>'
                : '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M4 14h-2v6h6v-2h-4v-4zm-2-4h2v-4h4v-2h-6v6zm16 8h-4v2h6v-6h-2v4zm-4-16v2h4v4h2v-6h-6z" fill="currentColor"/></svg>';
        };

        // Toggle fullscreen
        const toggleFullscreen = async () => {
            try {
                if (!document.fullscreenElement) {
                    await canvasContainer.requestFullscreen();
                } else {
                    await document.exitFullscreen();
                }
                updateFullscreenIcon();
            } catch (error) {
                console.error('Error toggling fullscreen:', error);
            }
        };

        // Add click handler
        fullscreenButton.addEventListener('click', toggleFullscreen);

        // Update icon when fullscreen state changes
        document.addEventListener('fullscreenchange', () => {
            updateFullscreenIcon();
            
            // Force canvas resize after a short delay to ensure proper dimensions
            setTimeout(() => {
                window.dispatchEvent(new Event('resize'));
            }, 100);
        });

        // Initial icon update
        updateFullscreenIcon();
    }

    /**
     * Set up effects UI
     */
    setupEffectsUI() {
        // Implementation of setupEffectsUI method
    }

    /**
     * Set up export buttons
     */
    setupExportButtons() {
        const exportGifButton = document.getElementById('exportGif');
        const exportVideoButton = document.getElementById('exportVideo');
        const progressBar = document.getElementById('exportProgress');
        const progressFill = progressBar.querySelector('.progress-fill');
        const progressText = progressBar.querySelector('.progress-text');

        // Handle GIF export
        const handleGifExport = async () => {
            try {
                progressBar.style.display = 'block';
                progressText.textContent = 'Preparing GIF...';
                progressFill.style.width = '0%';

                // Track export start
                if (typeof gtag !== 'undefined') {
                    gtag('event', 'export_start', {
                        'event_category': 'Export',
                        'event_label': 'gif'
                    });
                }

                // Start GIF export
                eventBus.emit('export:start', { type: 'gif' });
            } catch (error) {
                console.error('Failed to export GIF:', error);
                alert('Failed to export GIF. Please try again.');
                progressBar.style.display = 'none';
            }
        };

        // Handle video export
        const handleVideoExport = async () => {
            try {
                progressBar.style.display = 'block';
                progressText.textContent = 'Preparing video...';
                progressFill.style.width = '0%';

                // Track export start
                if (typeof gtag !== 'undefined') {
                    gtag('event', 'export_start', {
                        'event_category': 'Export',
                        'event_label': 'video'
                    });
                }

                // Start video export
                eventBus.emit('export:start', { type: 'video' });
            } catch (error) {
                console.error('Failed to export video:', error);
                alert('Failed to export video. Please try again.');
                progressBar.style.display = 'none';
            }
        };

        // Wire up traditional export buttons
        if (exportGifButton) {
            exportGifButton.addEventListener('click', handleGifExport);
        }
        if (exportVideoButton) {
            exportVideoButton.addEventListener('click', handleVideoExport);
        }

        // Wire up download modal options
        eventBus.on('export:gif', handleGifExport);
        eventBus.on('export:video', handleVideoExport);

        // Handle export progress
        eventBus.on('export:progress', ({ progress, text }) => {
            progressFill.style.width = `${progress}%`;
            progressText.textContent = text || `Exporting: ${Math.round(progress)}%`;
        });

        // Handle export completion
        eventBus.on('export:complete', ({ type, url }) => {
            progressBar.style.display = 'none';
            
            // Create temporary link to trigger download
            const link = document.createElement('a');
            link.href = url;
            link.download = `glitchify_export.${type === 'gif' ? 'gif' : 'mp4'}`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Track export completion
            if (typeof gtag !== 'undefined') {
                gtag('event', 'export_complete', {
                    'event_category': 'Export',
                    'event_label': type
                });
            }
        });

        // Handle export errors
        eventBus.on('export:error', ({ error }) => {
            console.error('Export error:', error);
            alert('Failed to export. Please try again.');
            progressBar.style.display = 'none';
        });
    }

    /**
     * Set up progress bar
     */
    setupProgressBar() {
        const progressBar = document.getElementById('exportProgress');
        const progressFill = progressBar.querySelector('.progress-fill');
        const progressText = progressBar.querySelector('.progress-text');

        this.showProgress = (text) => {
            progressBar.style.display = 'block';
            progressFill.style.width = '0%';
            progressText.textContent = text;
        };

        this.updateProgress = (progress, text) => {
            progressFill.style.width = `${progress}%`;
            progressText.textContent = text || `${Math.round(progress)}%`;
        };

        this.hideProgress = () => {
            progressBar.style.display = 'none';
        };
    }
} 